import React, { useEffect, useState } from "react";
import { Disclosure } from "@headlessui/react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { useLazyGetProfessionServicesQuery } from "../../../../../store/professions";
import IconButton from "../../../../iconButton";
import AdminProfessions from "../../../../modal/AdminProfessions";
import { useLocation } from "react-router-dom";
import { useAssignProfesionsMutation } from "../../../../../store/admin/professionalUserInfo";
import { toast } from "react-toastify";

const ProfessionalServicesProvided = ({
  professionalInfo,
  handleSelectServices,
}) => {
  const location = useLocation();
  const [results, setResults] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [removingProfessionId, setRemovingProfessionId] = useState(null);
  const [selectedProfessionServices, setSelectedProfessionServices] =
    useState(null);
  const [showProfessionModal, setShowProfessionModal] = useState(false);
  const [getProfessionServices] = useLazyGetProfessionServicesQuery();

  const [
    removeProfession,
    {
      isLoading: removeProfessionLoader,
      isSuccess: removeProfessionIsSuccess,
      data: removeProfessionData,
      isError: removeProfessionIsError,
      error: removeProfessionError,
    },
  ] = useAssignProfesionsMutation();

  useEffect(() => {
    if (
      !removeProfessionLoader &&
      removeProfessionData &&
      removeProfessionIsSuccess
    ) {
      setRemovingProfessionId(null)
      toast.success(removeProfessionData.message);
    }
    if (!removeProfessionLoader && removeProfessionIsError) {
      setRemovingProfessionId(null)
      toast.error(removeProfessionError.data.message);
    }
  }, [
    removeProfessionLoader,
    removeProfessionIsSuccess,
    removeProfessionIsError,
  ]);

  useEffect(() => {
    // Call the API with different IDs and update state variables with the results
    if (professionalInfo?.professions) {
      const fetchProfessionServices = async () => {
        const resultsArray = await Promise.all(
          professionalInfo.professions.map((profession) =>
            getProfessionServices(profession.id).then(
              (response) => response.data
            )
          )
        );
        setResults(resultsArray);
      };
      fetchProfessionServices();
    }
  }, [professionalInfo]);

  useEffect(() => {
    if (results.length > 0 && professionalInfo?.professions) {
      const newSelectedServices = results.map((result, index) => {
        const selectedProfession = professionalInfo.professions[index];
        return selectedProfession
          ? selectedProfession.services.map((item) => item)
          : [];
      });
      setSelectedServices(newSelectedServices);
    }
  }, [results, professionalInfo]);

  const handleCheckboxChange = (option, index) => {
    const updatedSelectedServices = [...selectedServices];
    const foundService = updatedSelectedServices[index].some(
      (service) => service.id === option.id
    );

    if (foundService) {
      updatedSelectedServices[index] = updatedSelectedServices[index].filter(
        (item) => item.id !== option.id
      );
    } else {
      updatedSelectedServices[index] = [
        ...updatedSelectedServices[index],
        option,
      ];
    }

    setSelectedServices(updatedSelectedServices);

    const allSelectedServices = updatedSelectedServices.flat();
    handleSelectServices && handleSelectServices(allSelectedServices);
  };

  const handleRemoveProfession = (profession) => {
    setRemovingProfessionId(profession.id);
    const params = {
      professionalId: professionalInfo?.id,
      professions: [profession.id],
      _method: "DELETE",
    };
    removeProfession(params);
  };

  const isGridCols2 = results.some((result) => result && result.length > 5);

  return (
    <>
      <div className="border border-solid border-[#EBEFF3] p-5 rounded-md mb-6 bg-white">
        <div className="flex justify-between md:items-center flex-col md:flex-row items-start">
          <h5 className="text-base font-bold leading-[160%] text-customGray mb-3 md:mb-0">
            Profession(s) and Services Provided
          </h5>
          <IconButton
            icon={PlusIcon}
            iconClasses="w-5 h-5 text-[#EF2121] text-primary"
            title="add profession"
            classes="text-base font-medium leading-[160%] text-primary flex items-center justify-center gap-1.5 capitalize cursor-pointer"
            onClick={() => setShowProfessionModal(true)}
          />
        </div>
        <div>
          {professionalInfo?.professions.map((item, index) => (
            <Disclosure
              as="div"
              className="profile-accordions py-6 border-b border-[#EBEFF3]"
              key={item.id}
              defaultOpen={true}
            >
              {({ open }) => (
                <>
                  <dt>
                    <Disclosure.Button
                      className="flex w-full items-start justify-between text-left text-gray-900 uppercase"
                      onClick={() => {
                        setSelectedProfessionServices(item.id);
                      }}
                    >
                      <div className="flex sm:items-center flex-col sm:flex-row gap-2">
                        <span className="text-xs font-bold leading-[160%] text-customGray">
                          {item.name}
                        </span>
                        <span className="text-xs font-bold leading-[160%] text-customGray hidden sm:block">
                          |
                        </span>
                        <span
                          className="text-xs font-bold leading-[160%] text-[#EF2121]"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveProfession(item);
                          }}
                        >
                          remove profession
                        {removeProfessionLoader && removingProfessionId === item.id && (
                          <span>
                            <svg
                              aria-hidden="true"
                              role="status"
                              className="inline w-5 h-5 me-3 text-primary animate-spin ml-3 mt-[2px]"
                              viewBox="0 0 100 101"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="#E5E7EB"
                              />
                              <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentColor"
                              />
                            </svg>
                          </span>
                        )}
                        </span>
                      </div>
                      <span className="ml-6 flex h-7 items-center">
                        {open ? (
                          <ChevronUpIcon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                        ) : (
                          <ChevronDownIcon
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    </Disclosure.Button>
                  </dt>
                  <Disclosure.Panel as="dd" className="mt-4">
                    <div>
                      <h6 className="text-xs font-bold leading-[160%] text-[#A7AFB7] uppercase mb-3.5">
                        Services Offered
                      </h6>
                      <ul
                        className={`grid ${
                          isGridCols2
                            ? "grid-cols-1 sm:grid-cols-2 xxl:grid-cols-3"
                            : "grid-cols-1"
                        } gap-3`}
                      >
                        {results[index]?.map((option, idx) => (
                          <li
                            className="form-group flex items-center"
                            key={option.id}
                          >
                            <div className="flex items-center relative">
                              <input
                                id={`services${index}-option-${idx}`}
                                aria-describedby="comments-description"
                                name={`services-option-${idx}`}
                                type="checkbox"
                                value={option.id}
                                checked={selectedServices[index]?.some(
                                  (service) => service.id === option.id
                                )}
                                onChange={() =>
                                  handleCheckboxChange(option, index)
                                }
                                className="rounded border border-solid border-[#CCD0D4] relative cursor-pointer inline-block w-5 h-5 leading-[0px]"
                              />
                            </div>
                            <div className="min-w-0 flex-1 text-sm leading-6 pl-[14px]">
                              <label
                                htmlFor={`services${index}-option-${idx}`}
                                className="font-medium text-customGray text-sm md:text-base leading-[160%] cursor-pointer"
                              >
                                {option.name}
                              </label>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          ))}
        </div>
      </div>
      <AdminProfessions
        professionalInfo={professionalInfo}
        professionalId={location?.state?.professionalId}
        showModal={showProfessionModal}
        setShowModal={setShowProfessionModal}
      />
    </>
  );
};

export default ProfessionalServicesProvided;
